import { useEffect, useRef, useState } from 'react'
import { pdfjs } from '../pdfjs'

export interface RenderedSize {
  /** CSS pixel size of the rendered page, which is the space overlays live in. */
  width: number
  height: number
}

interface Props {
  /** URL the PDF is fetched from. */
  src: string
  /** 1-based page to display. */
  page: number
  /** Called whenever the rendered size changes, so overlays can follow it. */
  onRendered?: (size: RenderedSize) => void
  onPageCount?: (count: number) => void
  onError?: (message: string) => void
  /** Absolutely-positioned overlay drawn on top of the page. */
  children?: React.ReactNode
  maxWidth?: number
}

/**
 * PdfView renders one page of a PDF to a canvas.
 *
 * The canvas backing store is sized by devicePixelRatio for sharpness while its
 * CSS size stays in layout pixels. Overlay rectangles are expressed as
 * fractions of the CSS size, so nothing downstream has to know about DPR.
 */
export function PdfView({ src, page, onRendered, onPageCount, onError, children, maxWidth = 900 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<RenderedSize>({ width: 0, height: 0 })
  const [loading, setLoading] = useState(true)
  const [doc, setDoc] = useState<pdfjs.PDFDocumentProxy | null>(null)
  // The width the page has to fit, followed as it changes. The page was drawn
  // once at the width it first had, so a window narrowed after it loaded (a
  // tablet turned, a browser made smaller) cut the page off at the right.
  const [room, setRoom] = useState(0)
  const renderTask = useRef<pdfjs.RenderTask | null>(null)

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.floor(entry.contentRect.width)
      setRoom((prev) => (prev === width ? prev : width))
    })
    observer.observe(wrap)
    return () => observer.disconnect()
  }, [])

  // The document, once per source. Fetched apart from the drawing, so a change
  // of width draws the page again without fetching the file again.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setDoc(null)
    // The signer's document is behind their signing cookie, and the API is on
    // another host in every deployed environment, so the fetch has to opt into
    // sending it. A blob: URL ignores this. Held so the worker and its buffers
    // are released when the view changes; in pdf.js v6 teardown lives on the
    // loading task, not the document.
    const loadingTask = pdfjs.getDocument({ url: src, withCredentials: true })
    loadingTask.promise.then((loaded) => {
      if (cancelled) return
      onPageCount?.(loaded.numPages)
      setDoc(loaded)
    }, (err: unknown) => {
      if (cancelled) return
      setLoading(false)
      onError?.(err instanceof Error ? err.message : 'This document could not be displayed.')
    })
    return () => {
      cancelled = true
      void loadingTask.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  useEffect(() => {
    if (!doc) return
    let cancelled = false

    async function render(d: pdfjs.PDFDocumentProxy) {
      try {
        const target = Math.min(maxWidth, room || wrapRef.current?.clientWidth || maxWidth)
        const pdfPage = await d.getPage(Math.min(Math.max(page, 1), d.numPages))
        if (cancelled) return

        // The unscaled viewport already accounts for /Rotate, so the rectangle
        // a user drags is in the same space the server transforms from.
        const base = pdfPage.getViewport({ scale: 1 })
        const scale = target / base.width
        const viewport = pdfPage.getViewport({ scale })

        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        renderTask.current?.cancel()
        const dpr = window.devicePixelRatio || 1
        canvas.width = Math.floor(viewport.width * dpr)
        canvas.height = Math.floor(viewport.height * dpr)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

        renderTask.current = pdfPage.render({ canvas, canvasContext: ctx, viewport })
        await renderTask.current.promise
        if (cancelled) return

        const next = { width: viewport.width, height: viewport.height }
        setSize(next)
        onRendered?.(next)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        // A cancelled render is routine when the page or the width changes mid-flight.
        const name = (err as { name?: string })?.name
        if (name === 'RenderingCancelledException') return
        setLoading(false)
        onError?.(err instanceof Error ? err.message : 'This document could not be displayed.')
      }
    }

    void render(doc)
    return () => {
      cancelled = true
      renderTask.current?.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, page, maxWidth, room])

  return (
    <div className="pdf-wrap" ref={wrapRef}>
      <div className="pdf-stage" style={{ width: size.width || undefined, height: size.height || undefined }}>
        <canvas ref={canvasRef} className="pdf-canvas" />
        {loading && <div className="pdf-loading">Rendering page…</div>}
        {size.width > 0 && children}
      </div>
    </div>
  )
}
